import { createFileRoute } from "@tanstack/react-router";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import headshot from "@/assets/chris-gould.jpg.asset.json";

const PUBLISHED = "2026-09-20T00:00:00+00:00";
const MODIFIED = "2026-10-03T00:00:00+00:00";

const TITLE = "About Square Migration | From Squarespace Migrations to Lead Gen Tools";
const DESCRIPTION =
  "Meet Chris Gould, founder of Square Migration. He combines 20 years of marketing, analytics and sales enablement experience to build useful digital experiences for B2B companies.";

const TIMELINE = [
  {
    year: "2021",
    title: "Square Migration is founded",
    body: "The business starts where the name says: migrating websites from WordPress and other platforms to Squarespace, full replatforming, careful redirect planning to preserve SEO equity, training so clients can run their own site, and options for ongoing maintenance.",
  },
  {
    year: "The idea behind it",
    title: "Easier to use. Fully owned.",
    body: "Every migration had the same goal: move clients onto a platform they can actually control and maintain themselves, where they own the content, the hosting relationship and the site outright.",
  },
  {
    year: "Since then",
    title: "The offering expanded. The name hasn't (yet).",
    body: "Clients kept asking for more than a website. Today Square Migration builds calculators, assessments, visualizers, custom apps and high-impact landing pages. They can live on a Squarespace site, another website platform or as a standalone experience. The focus is always the same: give people a useful reason to engage.",
  },
];


const MILESTONES = [
  { value: "2021", label: "Founded in Apple Valley, MN" },
  { value: "20+", label: "Years in marketing & analytics" },
  { value: "Top 10%", label: "Builder on the Lovable platform" },
  { value: "Squarespace", label: "Circle Member" },
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/about" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/about" }],
    scripts: [
      webPageSchemaScript({
        path: "/about",
        name: TITLE,
        description: DESCRIPTION,
        published: PUBLISHED,
        modified: MODIFIED,
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Chris Gould",
          jobTitle: "Founder & Owner",
          worksFor: { "@type": "Organization", name: "Square Migration" },
          url: "https://www.squaremigration.com/about",
          sameAs: ["https://www.linkedin.com/in/chrisgouldmn"],
          description:
            "Founder of Square Migration with more than 20 years of marketing, analytics and sales enablement experience, and a top 10% builder on the Lovable platform.",
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="About Us"
        title="We started with Squarespace migrations. Today, we also build the tools and campaigns that capture a prospects interest."
        description="Square Migration was founded in 2021 to help businesses take control of their websites. Today, we turn B2B expertise into calculators, assessments, visualizers, custom apps and focused campaigns that give prospects a reason to engage."
      />

      <section className="px-6 md:px-8 lg:px-12 py-16 md:py-20 border-t border-border/50 bg-[#080E1E]/95 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {TIMELINE.map((item) => (
              <div
                key={item.title}
                className="flex flex-col rounded-2xl bg-card/30 border border-border/50 p-6"
              >
                <p className="text-xs font-semibold tracking-[0.22em] uppercase text-primary mb-3">
                  {item.year}
                </p>
                <h2 className="text-xl font-semibold mb-3">{item.title}</h2>
                <p className="text-muted-foreground leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {MILESTONES.map((m) => (
              <div
                key={m.label}
                className="rounded-2xl border border-border/50 bg-card/20 p-5 text-center"
              >
                <p className="text-2xl font-semibold text-primary">{m.value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-8 lg:px-12 py-16 md:py-20 border-t border-border/50">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-start">
          <div className="flex flex-col items-start gap-6">
            <img
              src={headshot.url}
              alt="Chris Gould, Founder of Square Migration"
              className="w-full max-w-sm rounded-2xl border border-border/50 object-cover aspect-square"
              loading="lazy"
            />
            <div>
              <p className="text-lg font-semibold">Chris Gould</p>
              <p className="text-sm text-muted-foreground">Founder &amp; Owner</p>
              <a
                href="https://www.linkedin.com/in/chrisgouldmn"
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex text-xs text-primary hover:underline"
              >
                Connect on LinkedIn
              </a>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-primary mb-4">
              The Person Behind It
            </p>
            <h2 className="text-3xl sm:text-4xl font-semibold leading-tight mb-6">
              Twenty years of marketing.{" "}
              <span className="text-primary">A builder's approach to leads.</span>
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed lg:text-lg">
              <p>
                Chris Gould has spent more than two decades across marketing, analytics, websites
                and sales enablement. His experience ranges from agency work for brands including
                General Mills, Microsoft and Electronic Arts to leading digital marketing for
                AndersenWindows.com.
              </p>
              <p>
                That background shapes every project. Chris looks beyond whether something works
                to how people find it, why they engage, what the business learns from that
                interaction and how it can lead to a better sales conversation. His work also
                includes a patent in cross-channel conversion tracking.
              </p>
              <p>
                Today Chris builds every Square Migration tool himself, and ranks in the top 10%
                of builders on the Lovable platform. He works with clients from the first idea
                through launch, turning their expertise into calculators, assessments,
                visualizers and apps that are genuinely useful to their audience.
              </p>
              <p>
                If you have an idea for a tool your prospects would actually find useful, or a
                website that needs a new home, he'd love to hear it.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ClosingCTA
        eyebrow="Let's Talk"
        headline={
          <>
            Have an idea worth{" "}
            <span className="text-primary">building?</span>
          </>
        }
        subhead="That's where every engagement starts."
        body="Bring the question your best prospects keep asking, or a website that's outgrown its platform. We'll tell you in one call what's worth building and what it should cost."
        ctaLabel="Meet Virtually"
      />
      <RelatedServices current="/about" />
      <Footer />
    </main>
  );
}
